import { ServiceBuilder } from '../service-builder'

import {
  BaseModel,
  ModelRepository,
  AppContext,
  HttpHelper,
  Utils,
  ObservableUtils,
  TypeOf,
  DependencyContainer,
} from '../../..'

import {
  ModelRepositoryAdapter,
  IServiceBuilderResult,
  ModelIncludeFactory,
  EntityMappingFactory,
  ModelIdentityMap
} from '../..'

import { IDataServiceConfig, DataServiceFactory, BackendBuilder } from '../'
import { ModelFactory } from '../../../data/factories/index'

export class DataServiceBuilder
  extends ServiceBuilder<ModelRepositoryAdapter<BaseModel>, IDataServiceConfig<BaseModel>> {

  private _dataServiceFactories: { cached: DataServiceFactory; uncached: DataServiceFactory; }

  constructor(
    private _apiRoot: string,
    private _appContext: AppContext,
    private _httpHelper: HttpHelper,
    _utils: Utils,
    private _observableUtils: ObservableUtils,
    private _modelIncludeFactory: ModelIncludeFactory,
    private _entityMappingFactory: EntityMappingFactory,
    _dependencyContainer: DependencyContainer,
    _idMap: ModelIdentityMap
  ) {
    super(_utils, _dependencyContainer, _idMap)
  }

  public build(): Array<IServiceBuilderResult<ModelRepositoryAdapter<BaseModel>>> {
    if (this._configs.size === 0) {
      throw new Error('No configurations provided!')
    }

    return this._utils.mapSet(this._configs, (c: IDataServiceConfig<BaseModel>) => {
      return this.buildClass(c.service)
    })
  }

  public buildClass<S extends ModelRepositoryAdapter<BaseModel>>(
    serviceKlass: TypeOf<S>
  ): IServiceBuilderResult<S> {
    const config = this._utils.findSet(this._configs, (conf) => conf.service === serviceKlass)
    if (!config) { throw new Error(`No configuration for '${serviceKlass.name}' provided!`) }

    return this._buildFromConfig(config) as IServiceBuilderResult<S>
  }

  protected onServiceConfigAdded() {
    super.onServiceConfigAdded()
    this._dataServiceFactories = this._createDataServiceFactories()
  }

  private _createModelFactory(): ModelFactory {
    return new ModelFactory(this._getModelClasses(), this._utils)
  }

  private _createRepositories() {
    const modelFactory = this._createModelFactory()
    return {
      cached: new ModelRepository(
        this._createBackendBuilder().withCache()
          .setModelIdentityMap(this._createOrGetIdentityMap()).build(),
        modelFactory
      ),
      uncached: new ModelRepository(
        this._createBackendBuilder().withoutCache()
          .setModelIdentityMap(this._createOrGetIdentityMap()).build(),
        modelFactory
      )
    }
  }

  private _createDataServiceFactories() {
    const repositories = this._createRepositories()
    return {
      cached: new DataServiceFactory(
        this._withAlternativeDependencies([ModelRepository, repositories.cached]),
        this._getServiceFactoryMethods()
      ),
      uncached: new DataServiceFactory(
        this._withAlternativeDependencies([ModelRepository, repositories.uncached]),
        this._getServiceFactoryMethods()
      )
    }
  }

  private _createBackendBuilder() {
    return new BackendBuilder(
      this._apiRoot,
      this._appContext,
      this._getModelClasses(),
      this._getEndpointMapping(),
      this._getEntityMappingConfigs(),
      this._getPresenterClasses(),
      this._httpHelper,
      this._utils,
      this._observableUtils,
      this._modelIncludeFactory,
      this._entityMappingFactory
    )
  }

  private _withAlternativeDependencies(...klassAndValue: Array<Array<TypeOf<any>|any>>) {
    const alternativeClasses = new Map()
    klassAndValue.forEach((kv) => alternativeClasses.set(kv[0], kv[1]))

    const alt = new DependencyContainer()
    alt['_classes'] = this._dependencyContainer['_classes']
    const originalGet = alt.get
    alt.get = (k: TypeOf<any>): any => {
      if (alternativeClasses.get(k)) {
        return alternativeClasses.get(k)
      }
      return originalGet.call(alt, k)
    }
    return alt
  }

  private _createOrGetIdentityMap(): ModelIdentityMap {
    return this._idMap || new ModelIdentityMap()
  }

  private _getPresenterClasses() {
    return this._utils.mapSet<IDataServiceConfig<BaseModel>, TypeOf<YaysonPresenter>>(
      this._configs as any, (entry: IDataServiceConfig<BaseModel>) => entry.presenter
    )
  }

  private _buildFromConfig(
    config: IDataServiceConfig <BaseModel>
   ): IServiceBuilderResult < ModelRepositoryAdapter <BaseModel>> {
     return {
      class: config.service,
      instance: (
        this._dataServiceFactories[config.backend] as DataServiceFactory
      ).create(
        config.service,
        ...(config.deps || [])
      )
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/creators/data-service-builder/data-service.builder.ts